import React, { useEffect, useState, useCallback } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import UserMenu from './UserMenu';
import useAuthStore from '../../store/authStore';
import useFeatureFlagStore from '../../store/featureFlagStore';
import organisationSettingsService from '../../services/organisationSettingsService';
import { invalidateWhitelabelCache } from '../../hooks/useWhitelabel';
// Logo replaced with futuristic text

interface NavItem {
  name: string;
  path: string;
  icon: string;
  enabled: boolean;
  adminOnly?: boolean;
  featureKey: string;
  isDivider?: boolean;
}

const Sidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuthStore();
  const { 
    isFeatureEnabled, 
    userPermissionsLoaded,
    initializeWithUser
  } = useFeatureFlagStore();
  const isAdmin = user?.role === 'admin';
  
  // Organization logo state
  const [organizationLogo, setOrganizationLogo] = useState<string | null>(null);
  
  // Whitelabel settings state
  const [productName, setProductName] = useState<string>('Lumina AI');
  const [whitelabelPrimaryLogo, setWhitelabelPrimaryLogo] = useState<string | null>(null);
  
  // Initialize user feature permissions
  useEffect(() => {
    if (user) {
      initializeWithUser();
    }
  }, [user, initializeWithUser]);

  // Load organization logo and whitelabel settings
  const loadOrganizationSettings = useCallback(async () => {
    if (!user) return;
    
    try {
      const settings = await organisationSettingsService.getOrganisationSettings();
      
      // Load organization logo
      if (settings.logoUrl) {
        const baseUrl = settings.logoUrl.startsWith('http') 
          ? settings.logoUrl 
          : `${(process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/api$/, '')}${settings.logoUrl}`;
        const logoUrl = `${baseUrl}?t=${Date.now()}`;
        setOrganizationLogo(logoUrl);
      } else {
        setOrganizationLogo(null);
      }
      
      // Load whitelabel product name
      if (settings.whitelabelProductName) {
        setProductName(settings.whitelabelProductName);
      } else {
        setProductName('Lumina AI');
      }
      
      // Load whitelabel primary logo
      if (settings.whitelabelPrimaryLogoUrl) {
        const baseUrl = settings.whitelabelPrimaryLogoUrl.startsWith('http') 
          ? settings.whitelabelPrimaryLogoUrl 
          : `${(process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/api$/, '')}${settings.whitelabelPrimaryLogoUrl}`;
        setWhitelabelPrimaryLogo(`${baseUrl}?t=${Date.now()}`);
      } else {
        setWhitelabelPrimaryLogo(null);
      }
    } catch (error) {
      console.error('Error loading organization settings:', error);
      setOrganizationLogo(null);
      setProductName('Lumina AI');
      setWhitelabelPrimaryLogo(null);
    }
  }, [user]);

  useEffect(() => {
    loadOrganizationSettings();
  }, [loadOrganizationSettings]);

  // Listen for organization logo and whitelabel updates
  useEffect(() => {
    const handleSettingsUpdate = () => {
      console.log('Sidebar: Organization settings updated event received');
      invalidateWhitelabelCache();
      loadOrganizationSettings();
    };

    window.addEventListener('organizationLogoUpdated', handleSettingsUpdate);
    window.addEventListener('whitelabelUpdated', handleSettingsUpdate);
    
    return () => {
      window.removeEventListener('organizationLogoUpdated', handleSettingsUpdate);
      window.removeEventListener('whitelabelUpdated', handleSettingsUpdate);
    };
  }, [loadOrganizationSettings]);
  
  const navItems: NavItem[] = [
    // Dashboard section
    { name: 'Dashboard', path: '', icon: '', enabled: true, featureKey: '', isDivider: true },
    { name: 'Dashboard', path: '/dashboard', icon: 'dashboard', enabled: true, featureKey: 'dashboard' },
    { name: 'Google Reviews Ops', path: '/google-reviews/ops', icon: 'analytics', enabled: true, featureKey: 'google_reviews_ops' },
    { name: 'Google Reviews Dashboard', path: '/google-reviews/dashboard', icon: 'reports', enabled: true, featureKey: 'google_reviews_dashboard' },
    { name: 'Executive Search', path: '/executive-search', icon: 'search', enabled: true, featureKey: 'executive_search' },
    { name: 'Executive Chat', path: '/executive-chat', icon: 'chat', enabled: true, featureKey: 'executive_chat' },
    { name: 'Excel AI', path: '/executive-xlsx-chat', icon: 'file-spreadsheet', enabled: true, featureKey: 'executive_xlsx_chat' },
    { name: 'Dashboard Config', path: '/dashboard-config', icon: 'settings', enabled: true, featureKey: 'dashboard_configuration' },
    
    // Data Submission section
    { name: 'Data Submission', path: '', icon: '', enabled: true, featureKey: '', isDivider: true },
    { name: 'Submit Data', path: '/submit-data', icon: 'upload', enabled: true, featureKey: 'submit_data' },
    { name: 'Submission Quality Check', path: '/submission-quality-check', icon: 'quality', enabled: true, featureKey: 'submission_quality_check' },
    { name: 'View Submissions', path: '/view-submissions', icon: 'view', enabled: true, featureKey: 'view_submissions' },
    { name: 'Dealer Groups', path: '/dealer-group-drag-drop', icon: 'groups', enabled: true, featureKey: 'dealer_group_maintenance' },
    
    // Third group - Custom Analytics
    { name: 'Custom Analytics', path: '', icon: '', enabled: true, featureKey: '', isDivider: true },
    { name: 'Import Data', path: '/upload-csv', icon: 'teams', enabled: true, featureKey: 'upload_csv' },
    { name: 'Data Mapping', path: '/map-data', icon: 'mapping', enabled: true, featureKey: 'map_data' },
    { name: 'Financial Data', path: '/financial-data', icon: 'data', enabled: true, featureKey: 'financial_data' },
    { name: 'Analytics', path: '/analytics', icon: 'analytics', enabled: true, featureKey: 'analytics' },
    { name: 'Validation Rules', path: '/kpi-maintenance', icon: 'kpi', enabled: true, featureKey: 'kpi_maintenance' },
    { name: 'Run Validation Rules', path: '/kpi-analytics', icon: 'charts', enabled: true, featureKey: 'kpi_analytics' },
    { name: 'Generate AI Validations', path: '/generate-ai-validations', icon: 'analytics', enabled: true, featureKey: 'generate_ai_validations' },
    { name: 'Sales Data Demo', path: '/sales-data-demo', icon: 'data', enabled: true, featureKey: 'sales_data_demo' },
    { name: 'Market Sales Import', path: '/manufacturer-sales-import', icon: 'upload', enabled: true, featureKey: 'manufacturer_sales_import' },
    { name: 'Rental Utilization Import', path: '/rental-utilization-import', icon: 'upload', enabled: true, featureKey: 'rental_utilization_import' },
    
    // Fourth group - Reporting
    { name: 'Reporting', path: '', icon: '', enabled: true, featureKey: '', isDivider: true },
    { name: 'Reports', path: '/quarterly-reports', icon: 'reports', enabled: true, featureKey: 'quarterly_reports' },
    
    // Fifth group - Administration
    { name: 'Administration', path: '', icon: '', enabled: true, featureKey: '', isDivider: true },
    { name: 'Organisation Management', path: '/organisation-management', icon: 'building', enabled: true, featureKey: 'organisation_management' },
    { name: 'Organisation KPIs', path: '/organisation-kpis', icon: 'analytics', enabled: true, featureKey: 'organisation_kpis' },
    { name: 'Organisation KPI Rules', path: '/organisation-kpi-rules', icon: 'rules', enabled: true, featureKey: 'organisation_kpi_rules' },
    { name: 'Super Admin', path: '/super-admin', icon: 'crown', enabled: true, featureKey: 'super_admin' },
    { name: 'Hierarchy Management', path: '/hierarchy-management', icon: 'hierarchy', enabled: true, adminOnly: true, featureKey: 'hierarchy_management' },
    { name: 'Location Management', path: '/location-management', icon: 'location', enabled: true, featureKey: 'location_management' },
    { name: 'User Management', path: '/user-maintenance', icon: 'user-admin', enabled: true, adminOnly: true, featureKey: 'user_maintenance' },
    { name: 'User Access Management', path: '/user-access-management', icon: 'user-access', enabled: true, adminOnly: true, featureKey: 'user_access_management' },
    { name: 'User Questions', path: '/user-questions', icon: 'question', enabled: true, featureKey: 'user_questions' },
    { name: 'Location-Hierarchy Links', path: '/location-hierarchy', icon: 'link', enabled: true, adminOnly: true, featureKey: 'location_hierarchy' },
    { name: 'Group User Management', path: '/group-user-management', icon: 'group-admin', enabled: true, featureKey: 'group_user_maintenance' },
    { name: 'Role Management', path: '/role-maintenance', icon: 'roles', enabled: true, adminOnly: true, featureKey: 'role_maintenance' },
  ];
  
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Helper function to check if a section has any accessible items
  const sectionHasAccessibleItems = (sectionIndex: number): boolean => {
    // Find the next divider or end of array
    let nextDividerIndex = navItems.length;
    for (let i = sectionIndex + 1; i < navItems.length; i++) {
      if (navItems[i].isDivider) {
        nextDividerIndex = i;
        break;
      }
    }

    // Check if any items in this section are accessible
    for (let i = sectionIndex + 1; i < nextDividerIndex; i++) {
      const item = navItems[i];
      
      // Check admin-only restrictions
      if (item.adminOnly && user?.role !== 'admin' && !user?.organization_id) {
        continue;
      }
      
      // Check super admin restrictions
      if (item.path === '/super-admin' && !user?.is_super_admin) {
        continue;
      }
      
      // Check feature access
      const hasFeatureAccess = item.featureKey ? isFeatureEnabled(item.featureKey) : true;
      if (hasFeatureAccess) {
        return true; // Found at least one accessible item
      }
    }
    
    return false; // No accessible items in this section
  };
  
  // Wait until feature flags are loaded
  // Show loading while permissions are being fetched
  if (!userPermissionsLoaded && user) {
    return (
      <div className="dashboard-sidebar">
        <div className="sidebar-logo">
          <div className="logo-badge">
            <img 
              src={whitelabelPrimaryLogo || "/LuminaAilogoPNG.png"} 
              alt={productName} 
              className="sidebar-logo-image"
            />
          </div>
        </div>

        {organizationLogo && (
          <div className="organization-logo-section">
            <img 
              src={organizationLogo} 
              alt="Organization Logo" 
              className="organization-logo-full-width"
            />
          </div>
        )}
        
        <div className="loading-spinner"></div>
      </div>
    );
  }
  
  return (
    <div className="dashboard-sidebar">
      <div className="sidebar-logo">
        <div className="logo-badge">
          <img 
            src={whitelabelPrimaryLogo || "/LuminaAilogoPNG.png"} 
            alt={productName} 
            className="sidebar-logo-image"
          />
        </div>
      </div>

      {organizationLogo && (
        <div className="organization-logo-section">
          <img 
            src={organizationLogo} 
            alt="Organization Logo" 
            className="organization-logo-full-width"
          />
        </div>
      )}
      
      <nav className="sidebar-nav">
        <ul className="nav-list">
          {navItems.map((item, index) => {
            // For dividers (section headers), check if the section has any accessible items
            if (item.isDivider) {
              const hasAccessibleItems = sectionHasAccessibleItems(index);
              if (!hasAccessibleItems) {
                return null; // Hide the entire section if no items are accessible
              }
              
              return (
                <li key={`divider-${index}`} className="nav-divider">
                  <div className="divider-label">{item.name}</div>
                </li>
              );
            }
            
            // For admin-only features, check if user is admin or organization admin
            // Organization admins are users with organization_id who can manage their org
            if (item.adminOnly && user?.role !== 'admin' && !user?.organization_id) {
              return null;
            }
            
            // Special check for Super Admin feature - only show to Super Admins
            if (item.path === '/super-admin' && !user?.is_super_admin) {
              return null;
            }
            
            // Check if user has access to this feature
            const isFeatureActive = item.featureKey ? isFeatureEnabled(item.featureKey) : true;
            
            // Render regular nav item
            return isFeatureActive && (
              <li key={item.path} className="nav-item">
                <NavLink 
                  to={item.enabled ? item.path : '#'} 
                  className={({ isActive }) => 
                    `nav-link ${isActive && item.enabled ? 'active' : ''} ${!item.enabled ? 'disabled' : ''}`
                  }
                  onClick={(e) => !item.enabled && e.preventDefault()}
                >
                  <span className={`nav-icon icon-${item.icon}`}></span>
                  <span className="nav-text">{item.name}</span>
                  {!item.enabled && <span className="coming-soon-badge">Soon</span>}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>
      
      {/* User Menu */}
      <UserMenu />
      
      <div className="sidebar-footer">
        <p>{productName} v1.0</p>
        <button className="sidebar-logout-button" onClick={handleLogout}>
          <span className="logout-icon icon-logout"></span>
          <span className="logout-text">Log Out</span>
        </button>
      </div>
    </div>
  );
};

export default Sidebar;